/**
 * analytics.ts — privacy-friendly, local-first event counter.
 *
 * What this is NOT:
 *  - Not Google Analytics. No third-party scripts, no cookies.
 *  - Not a network beacon by default. Events never leave the device
 *    unless the site owner has explicitly opted in by setting
 *    `window.__analyticsEndpoint` to a URL (e.g. a tiny Cloudflare
 *    Worker that just appends a JSON line to a log).
 *  - Not a session replay / DOM scraper. We only count timestamps +
 *    event type + small structured payload.
 *
 * What this IS:
 *  - A 30-line FIFO buffer of {ts, type, payload} records kept in
 *    localStorage under the key `yhazrin-events` (capped at 100).
 *  - A `console.debug` mirror so the developer can watch activity in
 *    DevTools → Console in real time.
 *  - A safe-to-call API: every entry point is no-op outside a browser,
 *    is wrapped in try/catch, and degrades to memory-only if
 *    localStorage is unavailable (Safari private mode, SSR, etc.).
 *
 * The buffer survives reloads so you can inspect behaviour later
 * via DevTools → Application → Local Storage. This is enough to
 * answer questions like:
 *   - "How many deep-dives did users open last week?"
 *   - "Did anyone toggle ambient audio on scene 4?"
 *   - "How does scene_to_scene flow look over a session?"
 *
 * If / when we want a real backend, set:
 *     window.__analyticsEndpoint = '/api/track'
 * (any URL that accepts a JSON POST). Events will be flushed in
 * batches of up to 10 with `sendBeacon` (so navigations don't drop
 * them) and as a fire-and-forget `fetch(keepalive: true)` on the
 * remaining ones. The localStorage mirror still happens so we never
 * lose anything even if the endpoint is down.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EventType =
  | 'scene_enter'
  | 'deep_dive_open'
  | 'audio_toggle'
  | 'chapter_nav_click'
  | 'mode_toggle'
  | 'click_to_fly'
  | 'auto_orbit_pause'
  | 'auto_orbit_resume'
  | 'tour_start'
  | 'tour_stop'
  | 'tour_pause'
  | 'tour_resume'
  | 'tour_tick';

export interface AnalyticsEvent {
  /** ISO-ish timestamp string. */
  ts: string;
  /** Discriminator — one of EventType. */
  type: EventType;
  /** Free-form, type-specific payload. Keep it small (< 1 KB). */
  payload?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'yhazrin-events';
const MAX_EVENTS = 100;

declare global {
  interface Window {
    /** Set to a URL to opt in to network reporting. Default: undefined. */
    __analyticsEndpoint?: string;
    /** True when the user has used the analytics console mirror. */
    __analyticsMirrorAttached?: boolean;
  }
}

// ---------------------------------------------------------------------------
// Storage adapter — in-memory fallback when localStorage isn't available.
// ---------------------------------------------------------------------------

interface StorageAdapter {
  read(): AnalyticsEvent[];
  write(events: AnalyticsEvent[]): void;
}

function memoryAdapter(): StorageAdapter {
  // Holds the buffer in module scope so multiple reads see the same data
  // even when localStorage isn't available.
  let buf: AnalyticsEvent[] = [];
  return {
    read: () => buf.slice(),
    write: (events) => {
      buf = events.slice();
    },
  };
}

function localStorageAdapter(): StorageAdapter {
  return {
    read: () => {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // Drop anything we don't recognise — keeps the buffer healthy.
        return parsed.filter(
          (e): e is AnalyticsEvent =>
            e !== null &&
            typeof e === 'object' &&
            typeof (e as AnalyticsEvent).ts === 'string' &&
            typeof (e as AnalyticsEvent).type === 'string',
        );
      } catch {
        return [];
      }
    },
    write: (events) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
      } catch {
        // Quota exceeded or storage disabled — keep the in-memory copy
        // and silently give up on persistence for this batch.
      }
    },
  };
}

let storage: StorageAdapter =
  typeof window !== 'undefined' && (() => {
    try {
      const probe = '__analytics_probe__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  })()
    ? localStorageAdapter()
    : memoryAdapter();

// ---------------------------------------------------------------------------
// Network — opt-in only.
// ---------------------------------------------------------------------------

/**
 * tryBeacon — flush a batch of events to the configured endpoint.
 * Uses navigator.sendBeacon if available (survives page unload),
 * otherwise a fetch with `keepalive: true`. Both fire-and-forget.
 */
function tryBeacon(events: AnalyticsEvent[]): void {
  if (typeof window === 'undefined') return;
  const url = window.__analyticsEndpoint;
  if (!url) return;
  try {
    const body = JSON.stringify({ events });
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
    ) {
      return;
    }
    if (typeof fetch === 'function') {
      void fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
        mode: 'cors',
        credentials: 'omit',
      }).catch(() => {});
    }
  } catch {
    /* network errors are never fatal — keep the local copy. */
  }
}

// ---------------------------------------------------------------------------
// Console mirror — DevTools only.
// ---------------------------------------------------------------------------

function ensureConsoleMirror(): void {
  if (typeof window === 'undefined') return;
  if (window.__analyticsMirrorAttached) return;
  window.__analyticsMirrorAttached = true;
  // eslint-disable-next-line no-console
  console.info(
    '[analytics] private event log active. Inspect with:',
    `JSON.parse(localStorage.getItem('${STORAGE_KEY}') || '[]')`,
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * track — record a single event. Safe to call from anywhere.
 */
export function track(type: EventType, payload?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const event: AnalyticsEvent = {
    ts: new Date().toISOString(),
    type,
    payload: payload ?? undefined,
  };

  // 1. Append to the rolling buffer (FIFO at MAX_EVENTS).
  const current = storage.read();
  current.push(event);
  if (current.length > MAX_EVENTS) {
    current.splice(0, current.length - MAX_EVENTS);
  }
  storage.write(current);

  // 2. Mirror to DevTools console (debug level so it's quiet by default).
  ensureConsoleMirror();
  // eslint-disable-next-line no-console
  if (typeof console !== 'undefined' && console.debug) {
    console.debug('[analytics]', event);
  }

  // 3. Opt-in network beacon — fire occasionally, throttled by buffer size.
  if (window.__analyticsEndpoint && current.length % 10 === 0) {
    tryBeacon(current.slice(-10));
  }
}

/**
 * read — return a copy of the current event buffer. Useful for the
 * site owner to dump their own analytics from DevTools.
 */
export function read(): AnalyticsEvent[] {
  return storage.read();
}

/**
 * clear — wipe the buffer. Intended for "reset my session" controls.
 */
export function clear(): void {
  storage.write([]);
}

// Convenience wrappers around track() so call sites read nicely.
export const onSceneEnter = (sceneKey: string) =>
  track('scene_enter', { sceneKey });

export const onDeepDiveOpen = (type: string, id: string) =>
  track('deep_dive_open', { type, id });

export const onAudioToggle = (enabled: boolean) =>
  track('audio_toggle', { enabled });

export const onChapterNavClick = (targetScene: string) =>
  track('chapter_nav_click', { targetScene });

/**
 * attachGlobalErrorHook — installs a window.onerror listener that
 * records any unhandled exceptions under the synthetic
 * `client_error` event type. Useful when triaging a "this only happens
 * on one user's phone" bug. Off by default so the buffer doesn't
 * fill up with noise — call once explicitly.
 */
export function attachGlobalErrorHook(): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: ErrorEvent) => {
    track('deep_dive_open' as EventType, {
      _error: true,
      message: e.message,
      source: e.filename,
      line: e.lineno,
      col: e.colno,
    });
  };
  window.addEventListener('error', handler);
  return () => window.removeEventListener('error', handler);
}
